import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCandles,
  getMarket,
  getOrderbook,
  getRisk,
  getTrades,
  listMarkets,
  type Candle,
  type MarketRow,
  type OrderbookSnapshot,
  type RiskSnapshot,
  type Ticker,
  type Trade as TradeRow,
} from "../api/markets";
import { ChartPanel } from "../components/ChartPanel";
import { OrderbookPanel } from "../components/OrderbookPanel";
import { OrderEntry } from "../components/OrderEntry";
import { RiskCard } from "../components/RiskCard";
import { getWsTicket } from "../api/auth";
import { subscribe, type SocketMessage } from "../ws/socketManager";
import { useAuthStore } from "../stores/auth";

export function Trade() {
  const { symbol = "BTCUSDT" } = useParams();
  const isAuthed = !!useAuthStore((s) => s.accessToken);
  const [market, setMarket] = useState<(MarketRow & { ticker: Ticker }) | null>(null);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [ob, setOb] = useState<OrderbookSnapshot | null>(null);
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [risk, setRisk] = useState<RiskSnapshot | null>(null);
  const [allMarkets, setAllMarkets] = useState<MarketRow[]>([]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      getMarket(symbol),
      getCandles(symbol, "1m", 240),
      getOrderbook(symbol, 20),
      getTrades(symbol, 30),
      getRisk(symbol),
    ])
      .then(([m, c, o, t, r]) => {
        if (!alive) return;
        setMarket(m);
        setCandles(c.candles);
        setOb(o);
        setTrades(t.trades);
        setRisk(r);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [symbol]);

  useEffect(() => {
    listMarkets().then(setAllMarkets).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const [o, t, r, m] = await Promise.all([
          getOrderbook(symbol, 20),
          getTrades(symbol, 30),
          getRisk(symbol),
          getMarket(symbol),
        ]);
        setOb(o);
        setTrades(t.trades);
        setRisk(r);
        setMarket(m);
      } catch {
        // ignore
      }
    }, 4000);
    return () => clearInterval(id);
  }, [symbol]);

  useEffect(() => {
    if (!isAuthed) return;
    let unsub: (() => void) | null = null;
    (async () => {
      unsub = await subscribe(
        `ticker:${symbol}`,
        async (chs) => (await getWsTicket(chs)).ticket,
        (msg: SocketMessage) => {
          if (msg.type !== "ticker") return;
          const t = msg.payload as Ticker;
          setMarket((prev) => (prev ? { ...prev, ticker: t, last: t.last } : prev));
        },
      );
    })();
    return () => {
      unsub?.();
    };
  }, [symbol, isAuthed]);

  if (!market) return <div className="page">Loading {symbol}…</div>;

  const t = market.ticker;
  return (
    <div className="page page-trade">
      <header className="trade-header">
        <div className="trade-symbol">
          <h1>{market.symbol}</h1>
          <span className="muted">{market.display_name}</span>
        </div>
        <div className="trade-quote">
          <div className="trade-last">${t.last.toFixed(2)}</div>
          <div className={t.change_24h_pct >= 0 ? "up" : "down"}>
            {t.change_24h_pct >= 0 ? "+" : ""}
            {t.change_24h_pct.toFixed(2)}% · 24h
          </div>
        </div>
        <div className="trade-stats">
          <div>
            <span className="muted">High</span>
            <strong>${t.high_24h.toFixed(2)}</strong>
          </div>
          <div>
            <span className="muted">Low</span>
            <strong>${t.low_24h.toFixed(2)}</strong>
          </div>
          <div>
            <span className="muted">Vol</span>
            <strong>${(t.volume_24h / 1_000_000).toFixed(2)}M</strong>
          </div>
        </div>
      </header>
      <div className="trade-grid">
        <aside className="trade-left">
          <h4>Symbols</h4>
          <ul className="symbol-list">
            {allMarkets.map((m) => (
              <li key={m.symbol} className={m.symbol === symbol ? "active" : ""}>
                <Link to={`/trade/${m.symbol}`}>
                  <span>{m.symbol}</span>
                  <span className={m.change_24h_pct >= 0 ? "up" : "down"}>
                    {m.change_24h_pct.toFixed(2)}%
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
        <section className="trade-center">
          <ChartPanel candles={candles} />
          <div className="trade-tabs">
            <h4>Recent trades</h4>
            <table className="trades-table">
              <tbody>
                {trades.slice(0, 20).map((tr) => (
                  <tr key={tr.id} className={tr.side === "buy" ? "up" : "down"}>
                    <td>{tr.price.toFixed(2)}</td>
                    <td className="num">{tr.qty.toFixed(4)}</td>
                    <td className="muted num">
                      {new Date(tr.ts * 1000).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <aside className="trade-right">
          <OrderbookPanel ob={ob} />
          <OrderEntry symbol={symbol} refPrice={t.last} />
        </aside>
      </div>
      {risk && (
        <div className="trade-bottom">
          <RiskCard risk={risk} />
        </div>
      )}
    </div>
  );
}
